import stringSanitizer from 'stringSanitizer';
/**
 * Capitalize first character
 * @param str {String}
 *  string to capitalize
 * @return {string}
 */
const capitalize = (str) => str
  .split(' ')
  .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : []))
  .join(' ');

/**
 * Decode url param into human readable string.
 * @param param {String}
 * @return {string}
 */
const decodeUrlParam = (param) => param
  .replace(/-/g, ' ');

/**
 * Encode user values into url param.
 * @param param {String}
 * @param language {String}
 * @return {string}
 */
const encodeUrlParam = (param, language) => stringSanitizer(
  param
    .replace(/[`~!@#$%^*_&|+=?;,".<>{}()[\]]/gi, '')
    // Replace slashes with spaces.
    .replaceAll(/\//g, ' ')
    // Replace spaces with one.
    .replaceAll(/  +/g, ' ')
    // Replace spaces with dashes.
    .replaceAll(/\s*-\s*|\s/g, '-')
    .toLowerCase(),
  language,
);

/**
 * Get and encode salary checker search params from url and its query.
 * @param path {String}
 * @param hash {String}
 *  Represents URL hash.
 * @returns {{occupation: (string|string), city: (string|string), hash: (string|string)}}
 */
const getSearchParams = (path = '', hash = '') => {
  const params = decodeURIComponent(path)
    .split('/')
    .map((param) => capitalize(decodeUrlParam(param)));
  return {
    occupation: {
      job_title: params[0] || '',
      job_desc: '',
    },
    region: params[1] || '',
    city: params[2] || '',
    salary: typeof hash === 'string' ? hash.replace(/\D+/g, '') : '',
  };
};

/**
 * Split Occupation title by '/' or by '&' and return array of string(s)
 * @param occupation {String}
 * @return {[String]}
 */
const splitOccupationTitle = (occupation) => {
  // Remove ' (*)' for better search results.
  let titles = occupation.toLowerCase()
    .replace(/\s\(.*?\)/gm, '')
    .split(' / ');

  if (titles.length === 1) {
    titles = titles[0].split(' & ');
  }

  return titles;
};

/**
 * Convert search parameters into URL query.
 *
 * @param searchParams {Object}
 * @param language {String}
 * @returns {String}
 */
const searchParamsToUrl = (searchParams, language) => {
  let hash = '';

  let urlParts = [
    typeof searchParams.occupation === 'object' ? searchParams.occupation.job_title : searchParams.occupation,
    searchParams.region,
    searchParams.city,
  ].filter((el) => el); // Remove empty elements.

  // If salary value not empty add a hash to the end of the URL.
  if (searchParams.salary) {
    hash = `#salary=${searchParams.salary}`;
  }
  urlParts = urlParts.map((s) => encodeUrlParam(s.trim(), language)).filter((n) => n);
  return `${urlParts.join('/')}/${hash}`;
};

/**
 * Find the best matching object in a list that corresponds to a search param value.
 *
 * @param value {String}
 * @param list {Array}
 * @param objectKey {String}
 * @param language {String}
 */
const findBestMatchingObject = (value, list, objectKey, language) => {
  for (let i = 0; i < list.length; i += 1) {
    if (encodeUrlParam(list[i][objectKey], language) === encodeUrlParam(value, language)) {
      return list[i];
    }
  }

  return null;
};

export {
  getSearchParams,
  encodeUrlParam,
  findBestMatchingObject,
  searchParamsToUrl,
  splitOccupationTitle,
};
